import { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { useForm } from 'react-hook-form';
import {
 Button,
 Stack,
 Image,
 Popover,
 PopoverTrigger,
 PopoverContent,
 PopoverCloseButton,
 PopoverHeader,
 PopoverBody,
 Text,
 Box,
 useDisclosure,
 PopoverFooter,
 Input,
 Flex,
 useToast,
} from '@chakra-ui/react';
import { chatCloud, chatCloudBasic } from '../../assets/new-images/icons';
import baseService from '../../services/base';
import { fetchData } from '../../services/fetch';

export default function ChatAnswersPopover({
 surveyId,
 question,
 userId,
 companyId,
 type,
 themeId,
 openChat = false
}) {
 const toast = useToast();
 const { isOpen, onToggle, onClose } = useDisclosure();
 const [localHasUnreadMessages, setLocalHasUnreadMessages] = useState(
  question?.adm_company_msg?.has_new_messages || null
 );

 const popoverRef = useRef(null);

 useEffect(() => {
  if (openChat) {
   onToggle();
   setTimeout(() => {
    if (popoverRef.current) {
     popoverRef.current.scrollIntoView({
      block: "center", 
      behavior: 'smooth'
     });
    }
   }, 0);
  }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [openChat])

 const getFeedbackUrl = () => {
  let url = `feedback/question?user=${userId}&company=${companyId}&type=admin_company&question=${question?.id_question}`;
  if (type !== 'simple' && type !== 'certification') {
   url += `&id_theme=${question?.id_theme || themeId}`;
  }
  return url;
 };

 const {
  data: feedbackData,
  isLoading,
  mutate,
 } = useSWR(isOpen ? getFeedbackUrl() : null, fetchData);

 // const { data } = useSWR(
 //   localHasUnreadMessages && isOpen
 //     ? `/feedback/question/${
 //         question?.id_question
 //       }?role=${2}&user=${userId}&company=${companyId}&survey=${surveyId}`
 //     : null,
 //   (url) =>
 //     fetcher(url).then((data) => {
 //       setLocalHasUnreadMessages(false);
 //       return data;
 //     })
 // );

 const {
  register,
  handleSubmit,
  resetField,
  getValues,
  formState: { isValid, isSubmitting },
 } = useForm();

 async function onSubmit() {
  try {
   const values = {
    id_company: +companyId,
    id_question: question?.id_question,
    id_role: 1,
    id_survey: +surveyId,
    id_user: userId,
    type: 'admin_company',
    text: getValues('text'),
    ...(question?.id_theme !== undefined && {
     id_theme: question?.id_theme,
    }),
    ...(!!themeId && {
     id_theme: Number(themeId),
    }),
   };
   await baseService.post('feedback', values);
   toast({
    title: 'Mensagem enviada com sucesso',
    status: 'success',
   });
   handleClose();
   mutate();
  } catch (err) {
   toast({
    title: 'Erro ao enviar',
    description: 'Não foi possível enviar sua mensagem, contate o suporte',
    status: 'error',
   });
  }
 }

 async function removeFeedback(id) {
  try {
   await baseService.delete(`feedback/${id}`);
   toast({
    title: 'Feedback removido com sucesso',
    status: 'success',
    containerStyle: {
     width: 'fit-content',
     maxWidth: '100%',
     bg: '#2F2F2F',
     fontWeight: 400,
     whiteSpace: 'nowrap',
    },
   });
   handleClose();
   mutate();
  } catch (err) {
   toast({
    title: 'Erro ao excluir',
    description: 'Não foi possível excluir sua mensagem, contate o suporte',
    status: 'error',
   });
  }
 }

 const handleClose = () => {
  resetField('text');
  onClose();
 };

 return (
  <Flex
   ref={popoverRef}
   height='44px'
   align='center'
   gap='14px'
   pos='absolute'
   top={['12px', '6']}
   right='6'
  >
   <Popover
    trigger='click'
    isOpen={isOpen && !isLoading}
    onClose={handleClose}
    placement='bottom-end'
    isLazy
    strategy='fixed'
   >
    <PopoverTrigger>
     <Button
      onClick={onToggle}
      bg='#F5F5FB'
      borderRadius='41.7px'
      w='fit-content'
      height={['32px', '44px']}
      p='0 12px'
      alignItems={'center'}
      justifyContent={'center'}
      _active={{
       bg: '#F5F5FB',
      }}
     >
      <Image
       src={localHasUnreadMessages ? chatCloud : chatCloudBasic}
       alt='Feedback chat'
       height={['21px', 'auto']}
       mr='8px'
      />
      <Text
       as='span'
       color='black'
       fontSize={['13px', '16px']}
       lineHeight='32px'
       fontWeight={600}
      >
       Chat
      </Text>
     </Button>
    </PopoverTrigger>

    <PopoverContent
     w={'450px'}
     borderRadius='8px'
     boxShadow='0px 0px 100px 100vw rgba(0, 0, 0, 0.20)'
     textAlign='left'
     overflow='hidden'
     minH='192px'
     maxH='450px'
    >
     <PopoverHeader
      bg='gray.100'
      h='40px'
      fontWeight={700}
      border='0'
      fontSize='sm'
      lineHeight='16.44px'
      p='12px'
     >
      Suporte
     </PopoverHeader>
     <PopoverCloseButton top='10px' />
     {feedbackData && (
      <PopoverBody p='16px 12px' maxH='352px' overflowY='scroll'>
       {feedbackData?.map((feedback) => (
        <Stack
         key={feedback?.id_feedback}
         spacing='8px'
         align={feedback?.role === 2 ? 'start' : 'end'}
         my='8px'
        >
         <Flex
          bg={feedback?.role === 2 ? '#F5F5FB' : 'rgba(0, 115, 187, 0.1)'}
          p='12px'
          w='374px'
          borderRadius='20px 20px 20px 0px'
          flexDir='column'
         >
          <Text
           fontSize='14px'
           fontWeight={400}
           color={feedback?.role === 2 ? '#909090' : 'blue.100'}
           whiteSpace='normal'
           lineHeight='20px'
           letterSpacing='0.1px'
          >
           {feedback?.text}
          </Text>

          {feedback?.role !== 2 && (
           <Text
            mt={'10px'}
            fontSize='sm'
            lineHeight={'20px'}
            letterSpacing={'0.1px'}
            color={'#292B30'}
           >
            Equipe GS
           </Text>
          )}
         </Flex>
         {feedback?.role !== 2 && (
          <Button
           variant='ghost'
           fontSize='13px'
           fontWeight={400}
           w='374px'
           h='22px'
           justifyContent='start'
           p={0}
           color='#EA582B'
           onClick={() => removeFeedback(feedback?.id_feedback)}
          >
           Remover feedback
          </Button>
         )}
        </Stack>
       ))}

       {!feedbackData?.length && !isLoading && (
        <Box bg='#F5F5FB' p='12px'>
         <Text
          fontSize='sm'
          color='#5C6066'
          whiteSpace='normal'
          lineHeight='20px'
          letterSpacing='0.1px'
          textAlign={'center'}
         >
          Inicie agora um bate papo com quem está respondendo essa pergunta.
         </Text>
        </Box>
       )}
      </PopoverBody>
     )}
     <PopoverFooter p={'8px 12px'} bg={'#F5F5FB'} h={'56px'} border='none'>
      <Flex align='center' gap={'16px'}>
       <Input
        w={'100%'}
        h={'40px'}
        fontSize='sm'
        borderRadius={'10px'}
        p={'8px 0px 8px 16px'}
        border='1px solid #E4E4E4'
        placeholder='Digite sua mensagem'
        bg={'white'}
        overflow={'scroll'}
        _placeholderShown={{
         fontWeight: 400,
         color: '#828282',
        }}
        {...register('text', { required: true })}
       />

       <Button
        type='submit'
        variant='primary'
        w={'106px'}
        minW='106px'
        h={'40px'}
        fontSize='sm'
        isDisabled={!isValid}
        isLoading={isSubmitting}
        onClick={handleSubmit(onSubmit)}
       >
        Enviar
       </Button>
      </Flex>
     </PopoverFooter>
    </PopoverContent>
   </Popover>
  </Flex>
 );
}
